import { PropertyFilterQuery } from '@cloudscape-design/collection-hooks';
import { DateRangePickerProps } from '@cloudscape-design/components';

import { getApiClient, publicClient } from '../../api/client';
import type { components } from '../../api/schema';
import { buildListQueryParams } from './query';

type DripContactOptOutRequest = components['schemas']['DripContactOptOutRequest'];

export async function optOutDripContact(uuid: string, body: DripContactOptOutRequest) {
  // Public endpoint - no auth needed
  const { data, error } = await publicClient.POST('/drip/contacts/{uuid}/opt-out', {
    params: { path: { uuid } },
    body,
  });
  if (error) throw new Error(`Failed to opt out drip contact: ${JSON.stringify(error)}`);
  return data;
}

// --- Admin/Dashboard functions ---

export async function getDripContacts(
  query: PropertyFilterQuery,
  page = 1,
  limit = 20,
  range?: DateRangePickerProps.Value | null,
  sortingField?: string,
  sortDirection?: 'asc' | 'desc',
  now = new Date(),
  textQuery?: string,
  scope?: { campaign_uuid?: string | null; zip_blast_uuid?: string | null },
) {
  const client = getApiClient();
  const params = buildListQueryParams({
    query, page, limit, range, now,
    rangeName: 'created_at',
    sortingField: sortingField ?? 'created_at',
    sortDirection: sortDirection ?? 'desc',
    textQuery,
  });
  const { data, error } = await client.GET('/drip/contacts', {
    params: {
      query: {
        ...params,
        campaign_uuid: scope?.campaign_uuid ?? undefined,
        zip_blast_uuid: scope?.zip_blast_uuid ?? undefined,
      },
    },
  });
  if (error) throw new Error(`Failed to fetch drip contacts: ${JSON.stringify(error)}`);
  return data;
}

export async function getDripContactMeta() {
  const client = getApiClient();
  const { data, error } = await client.GET('/drip/contacts/meta');
  if (error) throw new Error(`Failed to fetch drip contacts meta: ${JSON.stringify(error)}`);
  return data;
}

export async function getDripContact(uuid: string) {
  const client = getApiClient();
  const { data, error } = await client.GET('/drip/contacts/{uuid}', {
    params: { path: { uuid } },
  });
  if (error) throw new Error(`Failed to fetch drip contact: ${JSON.stringify(error)}`);
  return data;
}

export async function uploadDripContactsExcel(file: File) {
  const client = getApiClient();
  const { data, error } = await client.POST('/drip/contacts/create/excel', {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    body: { file } as any,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    bodySerializer(body: any) {
      const fd = new FormData();
      fd.append('file', body.file);
      return fd;
    },
  });
  if (error) throw new Error(`Failed to upload drip contacts: ${JSON.stringify(error)}`);
  return data;
}

export async function bulkUnsubscribeDripContacts(uuids: string[]) {
  const client = getApiClient();
  const { data, error } = await client.POST('/drip/contacts/bulk-opt-out', {
    body: { uuids },
  });
  if (error) throw new Error(`Failed to bulk unsubscribe: ${JSON.stringify(error)}`);
  return data;
}
